/**
 * Type definitions for PlayerPageHeader components
 *
 * Includes interfaces for indicator tiles, modals, and player identity display.
 * All types support the Container/View pattern with strict readonly properties.
 */

import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";

/** Valuation status enumeration for badge styling */
export type ValuationStatus =
  | "SEVERELY_UNDERVALUED"
  | "SEVERELY UNDERVALUED"
  | "UNDERVALUED"
  | "FAIRLY_VALUED"
  | "FAIRLY VALUED"
  | "OVERVALUED"
  | "SEVERELY_OVERVALUED"
  | "SEVERELY OVERVALUED";

/** Badge action mapping for valuation status */
export type ValuationBadgeAction =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "muted";

/**
 * Indicator metric types for the key indicator tiles.
 *
 * `potential` (SE-8154) is conditional: it is only pushed onto the rail when
 * the player has BOTH GPR Potential bounds, which is a designed subset of
 * roughly 10.8% of players. Every other member is always present, so the rail
 * is five tiles for most players and six for a covered one.
 */
export type IndicatorMetricType =
  | "gpr"
  | "potential"
  | "playerStyleFit"
  | "teamStyleFit"
  | "transferValue"
  | "monthsLeft";

/** Chart types used in indicator detail modals */
export type ChartType = "teamComparison" | "valuationBounds" | "none";

/**
 * Configuration for indicator tiles
 * @remarks Used to define display and behavior for each metric type
 */
export interface IndicatorConfig {
  readonly type: IndicatorMetricType;
  readonly label: string;
  readonly shortLabel: string;
  readonly description: string;
  readonly explanation: string;
  readonly chartType: ChartType;
}

/**
 * Props for IndicatorTile component View
 * @remarks Pure presentation props for a single indicator tile
 */
export interface IndicatorTileViewProps {
  readonly label: string;
  readonly value: string;
  readonly valueColor: string;
  /** Press handler. Omit for non-navigable tiles (e.g. Months Left) so they
   *  render as a plain, non-interactive card with no button cursor/hover. */
  readonly onPress?: () => void;
  readonly testID?: string;
  readonly isDark: boolean;
  readonly isSmallScreen?: boolean;
  /** Whether the tile should stretch to full width (no flex shrink) */
  readonly isFullWidth?: boolean;
  /** Whether to render as a compact inline chip (used in modal context) */
  readonly isCompact?: boolean;
  /**
   * Whether to render a right-arrow affordance + hover background fill on
   * desktop. Set true for the 4 primary KPIs (GPR, Player Style Fit, Team
   * Style Fit, Gemini Player Valuation) that have a routed breakdown page
   * (SE-5024). monthsLeft and any compact/mobile variant get plain tiles.
   */
  readonly showArrow?: boolean;
}

/**
 * Props for IndicatorTile component Container
 * @remarks Container handles press logic and metric formatting
 */
export interface IndicatorTileContainerProps {
  readonly type: IndicatorMetricType;
  readonly value: number | string | null | undefined;
  readonly onPress: (type: IndicatorMetricType) => void;
  readonly testID?: string;
}

/**
 * Individual indicator data for the rail
 */
export interface IndicatorData {
  readonly type: IndicatorMetricType;
  readonly value: number | string | null | undefined;
}

/**
 * Props for IndicatorRail component View
 * @remarks Pure presentation props for the horizontal indicator container
 */
export interface IndicatorRailViewProps {
  readonly indicators: readonly {
    readonly type: IndicatorMetricType;
    readonly value: string;
    readonly valueColor: string;
    readonly label: string;
    /** Press handler. Omit for non-navigable tiles (e.g. Months Left). */
    readonly onPress?: () => void;
    /** Whether this indicator's tile should show the routed-breakdown arrow. */
    readonly showArrow?: boolean;
  }[];
  readonly isDark: boolean;
  readonly isSmallScreen?: boolean;
  /** Whether to render as compact chips (used in modal context) */
  readonly isCompact?: boolean;
}

/**
 * Props for IndicatorRail component Container
 * @remarks Container handles indicator configuration and callbacks
 */
export interface IndicatorRailContainerProps {
  readonly gpr: number | null | undefined;
  readonly playerStyleFit: number | null | undefined;
  readonly teamStyleFit: number | null | undefined;
  readonly transferValueLow: number | null | undefined;
  readonly transferValueHigh: number | null | undefined;
  readonly contractExpires: string | null | undefined;
  readonly onIndicatorPress: (type: IndicatorMetricType) => void;
}

/**
 * Data structure for player metrics in modal
 * @remarks Contains all data needed for metric detail display
 */
export interface PlayerMetricData {
  readonly playerName: string;
  readonly value: number | null;
  readonly valuationLowerBound?: number | null;
  readonly valuationUpperBound?: number | null;
  readonly publicValuation?: number | null;
}

/**
 * Data structure for team average comparison
 * @remarks Used in team comparison charts
 */
export interface TeamAverageData {
  readonly teamName: string;
  readonly averageValue: number;
}

/**
 * Props for IndicatorDetailsModal component View
 * @remarks Pure presentation props for the metric detail popup
 */
export interface IndicatorDetailsModalViewProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description: string;
  readonly explanation: string;
  readonly chartType: ChartType;
  readonly playerValue: number | null;
  readonly playerName: string;
  readonly teamAverageValue?: number | null;
  readonly teamName?: string | null;
  readonly valuationLowerBound?: number | null;
  readonly valuationUpperBound?: number | null;
  readonly publicValuation?: number | null;
  readonly isDark: boolean;
}

/**
 * Props for IndicatorDetailsModal component Container
 * @remarks Container handles metric configuration lookup and data transformation
 */
export interface IndicatorDetailsModalContainerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly metricType: IndicatorMetricType | null;
  readonly player: PlayerDetailPlayerMetaFragment | null | undefined;
  readonly teamAverageData?: TeamAverageData | null;
}

/**
 * Props for PlayerIdentityBlock component View
 * @remarks Pure presentation props for player identity display
 */
export interface PlayerIdentityBlockViewProps {
  readonly playerName: string;
  readonly roleArchetype: string | null;
  /**
   * Secondary tactical role archetype. Accepted but NOT rendered: SE-6992 hid
   * the badge on small screens and SE-7531 extended that to big screens, so it
   * is hidden at every width and the identity header shows only the primary
   * role. Still plumbed through because the value comes from the shared player
   * fragment and other surfaces consume it. Null when the player has none.
   */
  readonly secondaryRoleArchetype: string | null;
  readonly valuationStatus: string | null;
  readonly valuationBadgeAction: ValuationBadgeAction;
  readonly onLoanFrom: string | null;
  readonly positions: string;
  readonly clubName: string | null;
  readonly age: string | null;
  readonly birthYear: string | null;
  readonly nationality: string | null;
  readonly nationalityFlagUrl: string | null;
  readonly agency: string | null;
  readonly isInjured: boolean;
  readonly injuredUntil: string | null;
  readonly preferredFoot: string | null;
  readonly limitedDataWarning: boolean;
  readonly isManual: boolean;
  readonly isDark: boolean;
  /** Whether the screen is mobile sized — switches layout to left-aligned */
  readonly isSmallScreen?: boolean;
  /** Whether to use compact spacing (used in modal context) */
  readonly isCompact?: boolean;
  /**
   * Watchlists this player is currently saved in. Renders the "Saved in
   * watchlists:" indicator (SE-5024) on desktop and the interactive pill row
   * (SE-5084) on mobile — empty array hides the row entirely.
   */
  readonly watchlists?: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  /**
   * SE-5084: invoked when a watchlist pill is tapped on mobile (only the 2+
   * list case renders tappable pills). Wired to the list-membership editor
   * sheet opener. Omitted on desktop, where the indicator is plain text.
   */
  readonly onWatchlistPress?: (watchlistId: string) => void;
}

/**
 * Props for PlayerIdentityBlock component Container
 * @remarks Container handles data extraction from player fragment
 */
export interface PlayerIdentityBlockContainerProps {
  readonly player: PlayerDetailPlayerMetaFragment | null | undefined;
  /**
   * Watchlists this player is currently saved in. Optional — when omitted
   * the indicator row is hidden.
   */
  readonly watchlists?: readonly {
    readonly id: string;
    readonly name: string;
  }[];
}

/**
 * Props for PlayerPageHeader component View
 * @remarks Pure presentation props for the full header layout
 */
export interface PlayerPageHeaderViewProps {
  readonly identityBlockProps: PlayerIdentityBlockViewProps;
  readonly indicatorRailProps: Omit<IndicatorRailViewProps, "isDark">;
  readonly isDark: boolean;
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether rendered inside a modal — skips breadcrumb, uses compact spacing */
  readonly isModal?: boolean;
}

/**
 * Props for PlayerPageHeader component Container
 * @remarks Container handles feature flag check and data orchestration
 */
export interface PlayerPageHeaderContainerProps {
  readonly player: PlayerDetailPlayerMetaFragment | null | undefined;
  readonly loading: boolean;
  readonly isGoBack?: boolean;
  /** Whether rendered inside a modal — threads through to view for compact layout */
  readonly isModal?: boolean;
}

/**
 * Props for PlayerPageHeaderSkeleton component View
 * @remarks Pure presentation props for the loading skeleton
 */
export interface PlayerPageHeaderSkeletonViewProps {
  readonly isDark: boolean;
}

/**
 * Feature flag key for the player page header
 */
export const PLAYER_PAGE_HEADER_FEATURE_FLAG = "ff_player_page_header_v1";
